import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { FaroAlert } from "@components/common/faro-alert";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { UserDisplay } from "@components/common/user-display";
import { RemoveMemberSuccessDescription } from "@components/remove-member-success-description";
import { isSuccessResponse } from "@context-providers/error-boundary/error-boundary-utils";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { BaseMemberProps } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { SdbCompany } from "@custom-types/sdb-company-types";
import { Dialog, CloseIcon } from "@flat-ui";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useToast } from "@hooks/use-toast";
import { Typography } from "@mui/material";
import { PayloadAction } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { fetchingGroupsFlagsSelector } from "@store/groups/groups-selector";
import { removeMemberFromGroup } from "@store/groups/groups-slice-thunk";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";
import { fetchingProjectsFlagsSelector } from "@store/projects/projects-selector";
import { removeMemberFromProject } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { removeMembersByEmails } from "@store/teams/teams-slice";
import {
  GroupMemberEvents,
  ProjectMemberEvents,
  TableType,
  WorkspaceMemberEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SHOW_ON_HOVER_CLASS } from "@utils/ui-utils";
import { useMemo, useState } from "react";
import { removeOne } from "@store/active-members/active-members-slice";

/** Generic prop for RemoveSingleMemberFromEntity */
interface GenericProps<T extends MemberTableSubject>
  extends BaseCompanyIdProps,
    BaseMemberProps {
  /** The entity name */
  entityName: T;
}

/** Props for removing a single member from project */
interface RemoveSingleMemberFromProjectProps extends GenericProps<"project"> {
  entity: SdbProject;
}

/** Props for removing a single member from group */
interface RemoveSingleMemberFromGroupProps extends GenericProps<"group"> {
  entity: FaroCoreApiTypes.GroupDetailResponse;
}

/** Props for removing a single member from company */
interface RemoveSingleMemberFromWorkspaceProps
  extends GenericProps<"workspace" | "activeMembers"> {
  entity: SdbCompany;
}

type RemoveMemberProps =
  | RemoveSingleMemberFromProjectProps
  | RemoveSingleMemberFromGroupProps
  | RemoveSingleMemberFromWorkspaceProps;

/**
 * Opens a dialog with the member to be removed from project or group.
 */
export function RemoveSingleMemberFromEntity({
  companyId,
  member,
  entity,
  entityName,
}: RemoveMemberProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { navigateToGroups } = useAppNavigation();
  const { trackEvent } = useTrackEvent();

  /* Stores the open status of the dialog. */
  const [isOpen, setIsOpen] = useState(false);

  /* Flags to know if the member is being removed from the project */
  const { isRemovingProjectMember } = useAppSelector(
    fetchingProjectsFlagsSelector
  );
  /* Flags to know if the member is being removed from the group */
  const { isRemovingGroupMember } = useAppSelector(fetchingGroupsFlagsSelector);
  const { isRemovingCompanyMember } = useAppSelector(
    fetchingMembersFlagsSelector
  );

  /** Whether the entity is currently being removed */
  const isProcessing: boolean = useMemo(() => {
    switch (entityName) {
      case "project":
        return isRemovingProjectMember;
      case "group":
        return isRemovingGroupMember;
      case "workspace":
      case "activeMembers":
        return isRemovingCompanyMember;
    }
  }, [
    entityName,
    isRemovingGroupMember,
    isRemovingProjectMember,
    isRemovingCompanyMember,
  ]);

  /** Display name for the entity */
  const entityDisplayName =
    entityName === "activeMembers" ? "active members" : entityName;

  /** Dialog title */
  const title = `Remove from ${entityDisplayName}`;

  /** Text to show when the member was removed */
  const successDescription = useMemo(() => {
    return (
      <RemoveMemberSuccessDescription
        member={member}
        subjectType={entityName}
        entityName={entity.name}
      />
    );
  }, [entity, entityName, member]);

  /**
   * Navigates to the groups page after the group has been removed
   * and notifies the user.
   */
  function onGroupRemoved(): void {
    navigateToGroups({ companyId });
    showToast({
      message: (
        <>
          Successfully removed group <var>{entity.name}</var>
        </>
      ),
      type: "success",
    });
  }

  /** Handles removing a project member */
  async function removeProjectMember(): Promise<PayloadAction<unknown>> {
    trackEvent({
      name: ProjectMemberEvents.removeMember,
      props: { dataType: TableType.projectMembers },
    });

    const result = await dispatch(
      removeMemberFromProject({
        companyId,
        member,
        projectId: entity.id,
      })
    );

    return result;
  }

  /** Handles removing a company member */
  async function removeCompanyMember(): Promise<PayloadAction<unknown>> {
    trackEvent({
      name: WorkspaceMemberEvents.removeMember,
      props: {
        dataType:
          entityName === "workspace"
            ? TableType.companyMembers
            : TableType.activeMembers,
      },
    });

    return dispatch(removeMemberFromCompany({ companyId, member }))
      .unwrap()
      .then((response) => {
        // Also remove the member from the active members slice
        dispatch(removeOne(member.identity));
        return dispatch(removeMembersByEmails([response.email]));
      });
  }

  /** Handles removing a group member */
  async function removeGroupMember(): Promise<PayloadAction<unknown>> {
    trackEvent({
      name: GroupMemberEvents.removeMember,
      props: { dataType: TableType.groupMembers },
    });

    const result = await dispatch(
      removeMemberFromGroup({
        companyId,
        member,
        groupId: entity.id,
        onGroupRemoved,
      })
    );

    return result;
  }

  /**
   * Removes the member from the project and notifies the result.
   */
  async function removeMember(): Promise<void> {
    let result: PayloadAction<unknown>;

    // Call the right function based on the entity name
    switch (entityName) {
      case "project":
        result = await removeProjectMember();
        break;
      case "group":
        result = await removeGroupMember();
        break;
      case "workspace":
      case "activeMembers":
        result = await removeCompanyMember();
        break;
    }

    // Errors are already handled by the slice, only notify about a successful result
    if (isSuccessResponse(result)) {
      showToast({
        message: `Member removed from ${entityName}`,
        description: successDescription,
        type: "success",
      });
    }

    setIsOpen(false);
  }

  return (
    <>
      {/* Button to open the dialog that it is only shown on hover on the table row */}
      <SphereTooltip
        title={title}
        dataTestId="REMOVE_USER_BUTTON"
        boxProps={{
          className: SHOW_ON_HOVER_CLASS,
          sx: {
            textAlign: "right",
            // This will be overridden by parent component using SHOW_ON_HOVER_CLASS
            visibility: "hidden",
          },
        }}
      >
        <SphereIconButton
          aria-label="more"
          aria-haspopup="true"
          component={CloseIcon}
          onClick={() => setIsOpen(true)}
        />
      </SphereTooltip>

      {/* Dialog with the details of the member to be removed */}
      <Dialog
        title={title}
        confirmText="Remove"
        open={isOpen}
        onConfirm={removeMember}
        onCancel={() => setIsOpen(false)}
        showSpinner={isProcessing}
      >
        {entityName === "group" && entity.members.length === 1 ? (
          <FaroAlert
            severity="warning"
            sx={{
              marginBottom: SPACE_ELEMENTS_OF_MODAL,
            }}
          >
            Removing <b>the last group member</b> will also delete the group if
            there are no projects assigned.
          </FaroAlert>
        ) : (
          <Typography
            sx={{
              fontSize: "14px",
              marginBottom: SPACE_ELEMENTS_OF_MODAL,
            }}
          >
            When removing member from {entityDisplayName} they will no longer be
            able to access this {entityDisplayName}. You can always invite this
            member again.
          </Typography>
        )}

        <UserDisplay member={member} />
      </Dialog>
    </>
  );
}
